import { requestAppUpdate } from "@/pwa/runtime";
import { ConnectionScreen } from "@/pwa/PwaUi";
import { Suspense, lazy, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate, Route, Routes } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { HomeScreen } from "@/features/home/HomeScreen";

import { BootLoader } from "./BootLoader";
import { KeycloakHandover } from "./KeycloakHandover";
import { Shell } from "./Shell";
import { useAuth } from "./auth";
import { fetchWorkspaceContext } from "./workspaceContext";
import type { WorkspaceContext } from "./workspaceContext";
import { useWorkspace } from "@/api/workspace";

// every screen but the landing one is fetched when it is first opened, so the
// first paint carries the shell alone
const BoqScreen = lazy(() => import("@/features/boq/BoqScreen")
    .then((module) => ({ default: module.BoqScreen })));
const ArchiveScreen = lazy(() => import("@/features/archive/ArchiveScreen")
    .then((module) => ({ default: module.ArchiveScreen })));
const EmailScreen = lazy(() => import("@/features/email/EmailScreen")
    .then((module) => ({ default: module.EmailScreen })));
const HandoverScreen = lazy(() => import("@/features/handover/HandoverScreen")
    .then((module) => ({ default: module.HandoverScreen })));
const HseScreen = lazy(() => import("@/features/hse/HseScreen")
    .then((module) => ({ default: module.HseScreen })));
const LettersScreen = lazy(() => import("@/features/letters/LettersScreen")
    .then((module) => ({ default: module.LettersScreen })));
const MeetingsScreen = lazy(() => import("@/features/meetings/MeetingsScreen")
    .then((module) => ({ default: module.MeetingsScreen })));
const ReportsScreen = lazy(() => import("@/features/reports/ReportsScreen")
    .then((module) => ({ default: module.ReportsScreen })));
const ReportBuilderScreen = lazy(() => import("@/features/reportBuilder/ReportBuilderScreen")
    .then((module) => ({ default: module.ReportBuilderScreen })));
const PaymentScreen = lazy(() => import("@/features/payment/PaymentScreen")
    .then((module) => ({ default: module.PaymentScreen })));
const CatalogueScreen = lazy(() => import("@/features/catalogue/CatalogueScreen")
    .then((module) => ({ default: module.CatalogueScreen })));
const DashboardScreen = lazy(() => import("@/features/dashboard/DashboardScreen")
    .then((module) => ({ default: module.DashboardScreen })));
const ExecutiveScreen = lazy(() => import("@/features/dashboard/ExecutiveScreen")
    .then((module) => ({ default: module.ExecutiveScreen })));
const ContractsScreen = lazy(() => import("@/features/contracts/ContractsScreen")
    .then((module) => ({ default: module.ContractsScreen })));

const TimelineScreen = lazy(() => import("@/features/timeline/TimelineScreen")
    .then((module) => ({ default: module.TimelineScreen })));
const AnnouncementsScreen = lazy(() => import("@/features/announcements/AnnouncementsScreen")
    .then((module) => ({ default: module.AnnouncementsScreen })));
const DiscussScreen = lazy(() => import("@/features/discuss/DiscussScreen")
    .then((module) => ({ default: module.DiscussScreen })));
const ManualScreen = lazy(() => import("@/features/manual/ManualScreen")
    .then((module) => ({ default: module.ManualScreen })));
const AiScreen = lazy(() => import("@/features/ai/AiScreen")
    .then((module) => ({ default: module.AiScreen })));
const ScheduleBoard = lazy(() => import("@/features/timeline/ScheduleBoard")
    .then((module) => ({ default: module.ScheduleBoard })));
const ProjectsScreen = lazy(() => import("@/features/projects/ProjectsScreen")
    .then((module) => ({ default: module.ProjectsScreen })));
const PortfoliosScreen = lazy(() => import("@/features/portfolios/PortfoliosScreen")
    .then((module) => ({ default: module.PortfoliosScreen })));
const SettingsScreen = lazy(() => import("@/features/settings/SettingsScreen")
    .then((module) => ({ default: module.SettingsScreen })));
const RequestScreen = lazy(() => import("@/features/requests/RequestScreen")
    .then((module) => ({ default: module.RequestScreen })));
const DesignDashboardScreen = lazy(() => import("@/features/design/DesignDashboardScreen")
    .then((module) => ({ default: module.DesignDashboardScreen })));
const DrawingEditorScreen = lazy(() => import("@/features/requests/DrawingEditorScreen")
    .then((module) => ({ default: module.DrawingEditorScreen })));

// the build the page was loaded against; the server tells us when it moved on
let loadedBuild: string | null = null;

function Workspace() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const workspace = useWorkspace();
    const context = useQuery<WorkspaceContext>({
        queryKey: ["workspace", "context", user?.id, user?.company?.id, workspace],
        queryFn: () => fetchWorkspaceContext(user?.id && user.company?.id
            ? { userId: user.id, companyId: user.company.id } : undefined),
        networkMode: "always",
        refetchOnWindowFocus: true,
    });

    useEffect(() => {
        const build = context.data?.build;
        if (!build) {
            return;
        }
        if (loadedBuild === null) {
            loadedBuild = build;
        } else if (loadedBuild !== build) {
            // Follow the deployed build silently.
            void requestAppUpdate();
        }
    }, [context.data?.build]);

    if (context.isLoading) {
        return <BootLoader />;
    }

    return (
        <Shell>
            <Suspense fallback={<BootLoader fullscreen={false} />}>
                <Routes>
                    <Route path="/" element={<HomeScreen />} />
                    <Route path="/requests/:slug" element={<RequestScreen />} />
                    <Route path="/projects" element={<ProjectsScreen />} />
                    <Route path="/portfolios" element={<PortfoliosScreen />} />
                    <Route path="/boq" element={<BoqScreen />} />
                    <Route path="/payments" element={<PaymentScreen />} />
                    <Route path="/letters" element={<LettersScreen />} />
                    <Route path="/meetings" element={<MeetingsScreen />} />
                    {/* the committees now sit inside the meetings application */}
                    <Route path="/committees" element={<Navigate to="/meetings?view=councils" replace />} />
                    <Route path="/hse" element={<HseScreen />} />
                    <Route path="/handover" element={<HandoverScreen />} />
                    <Route path="/archive" element={<ArchiveScreen />} />
                    <Route path="/reports" element={<ReportsScreen />} />
                    <Route path="/report-builder" element={<ReportBuilderScreen />} />
                    <Route path="/email" element={<EmailScreen />} />
                    <Route path="/dashboard" element={<DashboardScreen />} />
                    <Route path="/design-dashboard" element={<DesignDashboardScreen />} />
                    <Route path="/executive" element={<ExecutiveScreen />} />
                    <Route path="/staff" element={<DashboardScreen section="staff" />} />
                    <Route path="/contracts" element={<ContractsScreen />} />
                    <Route path="/timeline" element={<TimelineScreen />} />
                    <Route path="/announcements" element={<AnnouncementsScreen />} />
                    <Route path="/discuss" element={<DiscussScreen />} />
                    <Route path="/manual" element={<ManualScreen />} />
                    <Route path="/ai" element={<AiScreen />} />
                    <Route path="/timeline/board" element={<ScheduleBoard />} />
                    <Route path="/settings" element={<SettingsScreen />} />
                    {/* the settings sections used to live here, and links to
                        them are still in the wild */}
                    <Route path="/workflow" element={<SettingsScreen />} />
                    <Route path="/r/:slug" element={<CatalogueScreen />} />
                    <Route path="*"
                           element={<div className="pm-page">{t("app.notFound")}</div>} />
                </Routes>
            </Suspense>
        </Shell>
    );
}

export function App() {
    const { user, loading, loginUrl, connectionFailed, retryConnection } = useAuth();

    if (loading) {
        return <BootLoader />;
    }
    if (connectionFailed) return <ConnectionScreen retry={retryConnection} />;
    if (!user) {
        // Keycloak owns sign-in: nobody signs in here, so anyone without a
        // session is handed straight over rather than shown a form to fill in
        return <KeycloakHandover loginUrl={loginUrl} />;
    }
    return (
        <Suspense fallback={<BootLoader />}>
            <Routes>
                {/* the drawing review takes the whole page, as it did before */}
                <Route path="/requests/drawing/:requestId/editor"
                       element={<DrawingEditorScreen />} />
                <Route path="*" element={<Workspace />} />
            </Routes>
        </Suspense>
    );
}
